import React, { forwardRef, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import ExpansionPanel from '@johnlewispartnership/wtr-ingredients/dist/ExpansionPanel';
import TextCard from '../../components/TradingCell/TextCard';
import PromoLink from '../../components/TradingCell/PromoLink';
import ExpansionButton from '../../components/TradingCell/ExpansionButton';
import PromoImagery from '../../components/PromoImagery';
import { useClickToBuyContext } from '../../components/ClickToBuy';
import { usePromoClickEvent } from '../../hooks/use-promo-click-event';
import { iconShape, ImageType, TextAreaType } from '../../constants/data-shapes/TradingCell';
import style from '../../components/TradingCell/index.scss';
import { TRADING_CELL_NAME } from '../../constants/analyticsNames';
import { TRADING_CELL } from '../../constants/cmsResourceTypes';
import { getImageAltTextAndUrl } from './analytics/get-image-alt-text-and-url';
const TradingCell = forwardRef(({ analytics, analyticsTags, componentId, cqResponsiveClasses, defaultNumOfCells, externalImageSrc, gridPosition, icons, image, isCitrusAd = false, isInEditor = false, position, products, taxonomyLevel, textArea, onPromoClick, }, forwardedRef) => {
    const promoClickEvent = usePromoClickEvent();
    const imageRef = useRef();
    const analyticsRef = useRef();
    const { isAccordionOpen, setIsAccordionOpen, isInClickToBuy } = useClickToBuyContext();
    const visualGridPosition = isCitrusAd || isInClickToBuy ? position : `grid-${gridPosition + 1}`;
    const { link: { url = '', newWindow = false } = {} } = textArea;
    const oneCell = defaultNumOfCells === 'oneCell';
    const fourCells = defaultNumOfCells === 'fourCells';
    const imageryProps = {
        icons,
        image,
        portrait: false,
        externalImageSrc,
        imageRef,
        lazyLoad: gridPosition > 10,
    };
    const promoClickCallback = () => {
        onPromoClick === null || onPromoClick === void 0 ? void 0 : onPromoClick(componentId);
        promoClickEvent(Object.assign(Object.assign(Object.assign(Object.assign({}, analytics), { creative: 'Trading Module', id: componentId, name: analytics === null || analytics === void 0 ? void 0 : analytics.promoName, position }), getImageAltTextAndUrl(analyticsRef === null || analyticsRef === void 0 ? void 0 : analyticsRef.current)), { linkUrl: url, newWindow,
            products }), taxonomyLevel);
    };
    const imageComponent = (React.createElement(PromoLink, Object.assign({}, textArea, { promoClick: promoClickCallback }),
        React.createElement(PromoImagery, Object.assign({}, imageryProps))));
    return (React.createElement("div", Object.assign({ ref: forwardedRef, id: `trading-asset-${visualGridPosition}`, "data-testid": "trading-cell", className: classNames(style.tradingCell, cqResponsiveClasses, {
            [style.noSpacing]: isInClickToBuy,
            [style.cmsGridSpacing]: isInEditor && !isInClickToBuy,
        }) }, analyticsTags),
        React.createElement("div", { ref: analyticsRef, id: componentId, className: style.tradingCellInner, "data-testid": `tradingCell_${defaultNumOfCells}` },
            React.createElement("div", { className: style.expansionPanelFlex }, isInClickToBuy ? (React.createElement(ExpansionPanel, { isOpen: !isAccordionOpen, alwaysOpen: { xs: false, sm: true, md: true, lg: true, xl: true } }, imageComponent)) : (imageComponent)),
            React.createElement("div", { className: style.text },
                React.createElement(TextCard, Object.assign({}, textArea, { oneCell, fourCells, showButton: !isInClickToBuy, promoClick: promoClickCallback }))),
            isInClickToBuy && (React.createElement(ExpansionButton, { setIsOpen: setIsAccordionOpen, isOpen: isAccordionOpen })))));
});
TradingCell.displayName = TRADING_CELL_NAME;
TradingCell.resourceType = TRADING_CELL;
TradingCell.defaultProps = {
    cqResponsiveClasses: [],
    isCitrusAd: false,
    externalImageSrc: false,
    gridPosition: 0,
    isInEditor: false,
    products: [],
};
TradingCell.propTypes = {
    analyticsTags: PropTypes.shape({}),
    componentId: PropTypes.string.isRequired,
    isCitrusAd: PropTypes.bool,
    cqResponsive: PropTypes.shape({
        deviceBreakpoints: PropTypes.arrayOf(PropTypes.shape({
            layout: PropTypes.string,
            name: PropTypes.string,
            visible: PropTypes.bool,
            width: PropTypes.string,
            offset: PropTypes.string,
        })),
    }).isRequired,
    defaultNumOfCells: PropTypes.oneOf(['oneCell', 'twoCells', 'threeCells', 'fourCells']).isRequired,
    image: ImageType.isRequired,
    textArea: TextAreaType.isRequired,
    icons: PropTypes.shape({
        offerRoundel: PropTypes.shape(Object.assign({}, iconShape)),
        messageIcon: PropTypes.shape({
            display: PropTypes.bool,
            data: PropTypes.shape({
                message: PropTypes.oneOf(['none', 'New', 'Try']),
            }),
        }),
        brandIcon: PropTypes.shape(Object.assign(Object.assign({}, iconShape), { position: PropTypes.oneOf(['floated', 'pinned']) })),
    }).isRequired,
    analytics: PropTypes.shape({
        promoName: PropTypes.string,
    }).isRequired,
    cqResponsiveClasses: PropTypes.arrayOf(PropTypes.string),
    isInEditor: PropTypes.bool,
    gridPosition: PropTypes.number,
    position: PropTypes.string.isRequired,
    taxonomyLevel: PropTypes.string,
    externalImageSrc: PropTypes.bool,
    products: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string })),
    promoRef: PropTypes.shape({
        current: PropTypes.any,
    }),
};
export default TradingCell;
//# sourceMappingURL=index.js.map